import "core-js/modules/web.dom-collections.iterator.js";
import addToArray from "./addToArray.js";
import ForEach from "./ForEach.js";
import getAccessorByteStride from "./getAccessorByteStride.js";
import defined from "../../Core/defined.js";
import WebGLConstants from "../../Core/WebGLConstants.js";

/**
 * Adds default glTF values if they don't exist.
 *
 * @param {object} gltf A javascript object containing a glTF asset.
 * @returns {object} The modified glTF.
 *
 * @private
 */
function addDefaults(gltf) {
  ForEach.accessor(gltf, function (accessor) {
    if (defined(accessor.bufferView)) {
      var _accessor$byteOffset;
      accessor.byteOffset = (_accessor$byteOffset = accessor.byteOffset) !== null && _accessor$byteOffset !== void 0 ? _accessor$byteOffset : 0;
    }
  });
  ForEach.bufferView(gltf, function (bufferView) {
    if (defined(bufferView.buffer)) {
      var _bufferView$byteOffse;
      bufferView.byteOffset = (_bufferView$byteOffse = bufferView.byteOffset) !== null && _bufferView$byteOffse !== void 0 ? _bufferView$byteOffse : 0;
    }
  });
  ForEach.mesh(gltf, function (mesh) {
    ForEach.meshPrimitive(mesh, function (primitive) {
      var _primitive$mode;
      primitive.mode = (_primitive$mode = primitive.mode) !== null && _primitive$mode !== void 0 ? _primitive$mode : WebGLConstants.TRIANGLES;
      if (!defined(primitive.material)) {
        if (!defined(gltf.materials)) {
          gltf.materials = [];
        }
        const defaultMaterial = {
          name: "default"
        };
        primitive.material = addToArray(gltf.materials, defaultMaterial);
      }
    });
  });
  ForEach.accessorContainingVertexAttributeData(gltf, function (accessorId) {
    var _accessor$normalized;
    const accessor = gltf.accessors[accessorId];
    const bufferViewId = accessor.bufferView;
    accessor.normalized = (_accessor$normalized = accessor.normalized) !== null && _accessor$normalized !== void 0 ? _accessor$normalized : false;
    if (defined(bufferViewId)) {
      const bufferView = gltf.bufferViews[bufferViewId];
      bufferView.byteStride = getAccessorByteStride(gltf, accessor);
      bufferView.target = WebGLConstants.ARRAY_BUFFER;
    }
  });
  ForEach.accessorContainingIndexData(gltf, function (accessorId) {
    const accessor = gltf.accessors[accessorId];
    const bufferViewId = accessor.bufferView;
    if (defined(bufferViewId)) {
      const bufferView = gltf.bufferViews[bufferViewId];
      bufferView.target = WebGLConstants.ELEMENT_ARRAY_BUFFER;
    }
  });
  ForEach.material(gltf, function (material) {
    var _material$extensions, _material$emissiveFac, _material$alphaMode, _material$doubleSided;
    const extensions = (_material$extensions = material.extensions) !== null && _material$extensions !== void 0 ? _material$extensions : {};
    const materialsCommon = extensions.KHR_materials_common;
    if (defined(materialsCommon)) {
      var _values$transparency, _materialsCommon$tran, _materialsCommon$doub;
      const technique = materialsCommon.technique;
      const values = defined(materialsCommon.values) ? materialsCommon.values : {};
      materialsCommon.values = values;
      values.ambient = defined(values.ambient) ? values.ambient : [0.0, 0.0, 0.0, 1.0];
      values.emission = defined(values.emission) ? values.emission : [0.0, 0.0, 0.0, 1.0];
      values.transparency = (_values$transparency = values.transparency) !== null && _values$transparency !== void 0 ? _values$transparency : 1.0;
      if (technique !== "CONSTANT") {
        values.diffuse = defined(values.diffuse) ? values.diffuse : [0.0, 0.0, 0.0, 1.0];
        if (technique !== "LAMBERT") {
          var _values$shininess;
          values.specular = defined(values.specular) ? values.specular : [0.0, 0.0, 0.0, 1.0];
          values.shininess = (_values$shininess = values.shininess) !== null && _values$shininess !== void 0 ? _values$shininess : 0.0;
        }
      }

      // These actually exist on the extension object, not the values object despite what's shown in the spec
      materialsCommon.transparent = (_materialsCommon$tran = materialsCommon.transparent) !== null && _materialsCommon$tran !== void 0 ? _materialsCommon$tran : false;
      materialsCommon.doubleSided = (_materialsCommon$doub = materialsCommon.doubleSided) !== null && _materialsCommon$doub !== void 0 ? _materialsCommon$doub : false;
      return;
    }
    material.emissiveFactor = (_material$emissiveFac = material.emissiveFactor) !== null && _material$emissiveFac !== void 0 ? _material$emissiveFac : [0.0, 0.0, 0.0];
    material.alphaMode = (_material$alphaMode = material.alphaMode) !== null && _material$alphaMode !== void 0 ? _material$alphaMode : "OPAQUE";
    material.doubleSided = (_material$doubleSided = material.doubleSided) !== null && _material$doubleSided !== void 0 ? _material$doubleSided : false;
    if (material.alphaMode === "MASK") {
      var _material$alphaCutoff;
      material.alphaCutoff = (_material$alphaCutoff = material.alphaCutoff) !== null && _material$alphaCutoff !== void 0 ? _material$alphaCutoff : 0.5;
    }
    const techniquesExtension = extensions.KHR_techniques_webgl;
    if (defined(techniquesExtension)) {
      ForEach.materialValue(material, function (materialValue) {
        // Check if material value is a TextureInfo object
        if (defined(materialValue.index)) {
          addTextureDefaults(materialValue);
        }
      });
    }
    addTextureDefaults(material.emissiveTexture);
    addTextureDefaults(material.normalTexture);
    addTextureDefaults(material.occlusionTexture);
    const pbrMetallicRoughness = material.pbrMetallicRoughness;
    if (defined(pbrMetallicRoughness)) {
      var _pbrMetallicRoughness, _pbrMetallicRoughness2, _pbrMetallicRoughness3;
      pbrMetallicRoughness.baseColorFactor = (_pbrMetallicRoughness = pbrMetallicRoughness.baseColorFactor) !== null && _pbrMetallicRoughness !== void 0 ? _pbrMetallicRoughness : [1.0, 1.0, 1.0, 1.0];
      pbrMetallicRoughness.metallicFactor = (_pbrMetallicRoughness2 = pbrMetallicRoughness.metallicFactor) !== null && _pbrMetallicRoughness2 !== void 0 ? _pbrMetallicRoughness2 : 1.0;
      pbrMetallicRoughness.roughnessFactor = (_pbrMetallicRoughness3 = pbrMetallicRoughness.roughnessFactor) !== null && _pbrMetallicRoughness3 !== void 0 ? _pbrMetallicRoughness3 : 1.0;
      addTextureDefaults(pbrMetallicRoughness.baseColorTexture);
      addTextureDefaults(pbrMetallicRoughness.metallicRoughnessTexture);
    }
    const pbrSpecularGlossiness = extensions.KHR_materials_pbrSpecularGlossiness;
    if (defined(pbrSpecularGlossiness)) {
      var _pbrSpecularGlossines, _pbrSpecularGlossines2, _pbrSpecularGlossines3;
      pbrSpecularGlossiness.diffuseFactor = (_pbrSpecularGlossines = pbrSpecularGlossiness.diffuseFactor) !== null && _pbrSpecularGlossines !== void 0 ? _pbrSpecularGlossines : [1.0, 1.0, 1.0, 1.0];
      pbrSpecularGlossiness.specularFactor = (_pbrSpecularGlossines2 = pbrSpecularGlossiness.specularFactor) !== null && _pbrSpecularGlossines2 !== void 0 ? _pbrSpecularGlossines2 : [1.0, 1.0, 1.0];
      pbrSpecularGlossiness.glossinessFactor = (_pbrSpecularGlossines3 = pbrSpecularGlossiness.glossinessFactor) !== null && _pbrSpecularGlossines3 !== void 0 ? _pbrSpecularGlossines3 : 1.0;
      addTextureDefaults(pbrSpecularGlossiness.specularGlossinessTexture);
    }
  });
  ForEach.animation(gltf, function (animation) {
    ForEach.animationSampler(animation, function (sampler) {
      var _sampler$interpolatio;
      sampler.interpolation = (_sampler$interpolatio = sampler.interpolation) !== null && _sampler$interpolatio !== void 0 ? _sampler$interpolatio : "LINEAR";
    });
  });
  const animatedNodes = getAnimatedNodes(gltf);
  ForEach.node(gltf, function (node, id) {
    const animated = defined(animatedNodes[id]);
    if (animated || defined(node.translation) || defined(node.rotation) || defined(node.scale)) {
      var _node$translation, _node$rotation, _node$scale;
      node.translation = (_node$translation = node.translation) !== null && _node$translation !== void 0 ? _node$translation : [0.0, 0.0, 0.0];
      node.rotation = (_node$rotation = node.rotation) !== null && _node$rotation !== void 0 ? _node$rotation : [0.0, 0.0, 0.0, 1.0];
      node.scale = (_node$scale = node.scale) !== null && _node$scale !== void 0 ? _node$scale : [1.0, 1.0, 1.0];
    } else {
      var _node$matrix;
      node.matrix = (_node$matrix = node.matrix) !== null && _node$matrix !== void 0 ? _node$matrix : [1.0, 0.0, 0.0, 0.0, 0.0, 1.0, 0.0, 0.0, 0.0, 0.0, 1.0, 0.0, 0.0, 0.0, 0.0, 1.0];
    }
  });
  ForEach.sampler(gltf, function (sampler) {
    var _sampler$wrapS, _sampler$wrapT;
    sampler.wrapS = (_sampler$wrapS = sampler.wrapS) !== null && _sampler$wrapS !== void 0 ? _sampler$wrapS : WebGLConstants.REPEAT;
    sampler.wrapT = (_sampler$wrapT = sampler.wrapT) !== null && _sampler$wrapT !== void 0 ? _sampler$wrapT : WebGLConstants.REPEAT;
  });
  if (defined(gltf.scenes) && !defined(gltf.scene)) {
    gltf.scene = 0;
  }
  return gltf;
}
function getAnimatedNodes(gltf) {
  const nodes = {};
  ForEach.animation(gltf, function (animation) {
    ForEach.animationChannel(animation, function (channel) {
      const target = channel.target;
      const nodeId = target.node;
      const path = target.path;
      // Ignore animations that target 'weights'
      if (path === "translation" || path === "rotation" || path === "scale") {
        nodes[nodeId] = true;
      }
    });
  });
  return nodes;
}
function addTextureDefaults(texture) {
  if (defined(texture)) {
    var _texture$texCoord;
    texture.texCoord = (_texture$texCoord = texture.texCoord) !== null && _texture$texCoord !== void 0 ? _texture$texCoord : 0;
  }
}
export default addDefaults;